import { CDK_DROP_LIST, CdkDropList, DragDropModule } from '@angular/cdk/drag-drop';
import { CdkMenuModule } from '@angular/cdk/menu';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Inject, Input, OnChanges, OnDestroy, Optional, Output, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { FwDialogService, FwIconModule, FwTooltipModule } from '@flywheel-io/vision';
import { DirectivesModule } from 'app/src/common/directives.module';
import { ContainerModule } from 'app/src/containers/container.module';
import { FileModule } from 'app/src/files/file.module';
import { MaterialModule } from 'app/src/material.module';
import { PipesModule } from 'app/src/shared/pipes.module';
import { SharedModule } from 'app/src/shared/shared.module';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import sortBy from 'lodash/sortBy';
import { Observable, ReplaySubject,Subscription } from 'rxjs';
import { filter, first, map, startWith, switchMap, take } from 'rxjs/operators';

import { acquisitionLabelMaxLength, labelMinLength } from '../../app.constants';
import { ConfigService } from '../../config.service';
import { ContainerAttachmentsService } from '../../containers/attachments/container-attachments.service';
import { FileDownloadDialog } from '../../containers/file-download-dialog/file-download-dialog.component';
import { DialogService } from '../../dialogs';
import { DeleteDialogComponent, DeleteDialogResult } from '../../dialogs/delete-dialog/delete-dialog.component';
import { CoreApiService } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { getContainerProjectId } from '../../shared/container.utils';
import { ContainerType } from '../../shared/enums/container-type.enum';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { ReaderTaskContainerType } from '../../shared/enums/task.enum';
import { OriginInfo } from '../../shared/file.utils';
import { prohibitValues, validFileName } from '../../shared/form.utils';
import { Acquisition } from '../../shared/models/acquisition.model';
import { CVATSyncState, File, FileMoveBody } from '../../shared/models/file.model';
import { Session } from '../../shared/models/session.model';
import { ViewerApp } from '../../shared/models/site-settings.model';
import { firstValueFrom, tryOperations } from '../../shared/observable.utils';
import {
  CreateReaderTaskDialogComponent,
} from '../../task-manager/create-reader-task-dialog/create-reader-task-dialog.component';
import { TranslationBundle, TranslationService } from '../../translation.service';
import { AcquisitionUploadDialog } from '../../upload/acquisition-upload/acquisition-upload-dialog.component';
import { ViewerParameters } from '../../viewers/frame/viewer-frame.model';
import { ViewerService } from '../../viewers/viewer.service';
import { AcquisitionInfoDialog, AcquisitionInfoDialogResult } from '../acquisition-info-dialog/acquisition-info-dialog.component';
import { AcquisitionNotesDialog } from '../acquisition-notes-dialog/acquisition-notes-dialog.component';
import { SessionService } from '../session.service';
import { SessionDataState } from '../session-data/session-data.component';

@Component({
  selector: 'acquisition-files',
  templateUrl: './acquisition-files.component.html',
  styleUrls: ['./acquisition-files.component.scss'],
  standalone: true,
  imports: [
    CdkMenuModule,
    CommonModule,
    ContainerModule,
    DirectivesModule,
    DragDropModule,
    FileModule,
    FormsModule,
    FwIconModule,
    FwTooltipModule,
    MaterialModule,
    PipesModule,
    ReactiveFormsModule,
    RouterModule,
    SharedModule,
  ],
})
export class AcquisitionFilesComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChildren('fileTypeContainer') fileTypeContainer: QueryList<ElementRef>;
  @ViewChildren('fileActions', { read: ElementRef }) fileActionsElement!: QueryList<ElementRef>;

  @Input() acquisition: Acquisition
  @Input() checked?: boolean
  @Input() files?: File[] // filtered/sorted files
  @Input() session?: Session

  @Output() checkedChange = new EventEmitter<boolean>()
  @Output() stagedChanged = new EventEmitter<string | null>() // acquisition._id for save, or null for cancel

  showHoverIcons = false

  acquisitionFiles$ = new ReplaySubject<File[]>(1)
  disableFileDrag = false
  readonly features = {
    copyByReference: this.configService.isFeatureEnabled('copy_by_reference'),
    cvat: this.configService.isFeatureEnabled('cvat'),
    modifyMetadata: this.configService.isFeatureEnabled('containers_modify_metadata'),
    tasksRefactor: this.configService.isFeatureEnabled('tasks_refactor'),
  }
  fileRenames: Record<string, FormControl> = {}
  fileTypes: string[] = []
  fileViewParameters$: Observable<ViewerParameters>
  permissions = {
    deleteContainer: false,
    downloadFiles: false,
    modifyFileMetadata: false,
    moveFiles: false,
    renameFiles: false,
    tasksModify: false,
    uploadFiles: false,
    viewMetadata: true,
    viewNotes: false,
  }
  stagingForm = new FormGroup({
    label: new FormControl('', [Validators.required, Validators.minLength(labelMinLength), Validators.maxLength(acquisitionLabelMaxLength)]),
  })
  typeChanges: Record<string, FormControl> = {}
  viewerApps: ViewerApp[]

  private messages = TranslationBundle.EMPTY
  private subscriptions = {
    associatedApps: Subscription.EMPTY,
    fileTypes: Subscription.EMPTY,
    messages: Subscription.EMPTY,
    permissions: Subscription.EMPTY,
    renames: Subscription.EMPTY,
  };

  CVATSyncStateTooltipMap = {
    [CVATSyncState.Synced]: 'File synced - Ready for Annotation',
    [CVATSyncState.NotSynced]: 'File is syncing - Not ready for Annotation',
    [CVATSyncState.Error]: 'Sync Error - Annotation disabled',
  };

  CVATSyncStateClassMap = {
    [CVATSyncState.Synced]: 'synced-icon',
    [CVATSyncState.NotSynced]: 'not-synced-icon',
    [CVATSyncState.Error]: 'error-icon',
  };

  CVATSyncStateIconMap = {
    [CVATSyncState.Synced]: 'check_circle',
    [CVATSyncState.NotSynced]: 'cached',
    [CVATSyncState.Error]: 'error',
  };


  constructor(
    @Optional() @Inject(CDK_DROP_LIST) dropList: CdkDropList,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private sessionService: SessionService,
    private translate: TranslationService,
    private viewerService: ViewerService,
    public state: SessionDataState,
    private configService: ConfigService,
    private dialog: DialogService,
    private fwDialog: FwDialogService,
    private containerAttachments: ContainerAttachmentsService,
  ) {
    if (!dropList) {
      // only allow dragging if we're within a drop list
      this.disableFileDrag = true;
    }

    this.subscriptions.permissions = this.state.projectPermissions$.subscribe(permissions => {
      this.permissions = {
        deleteContainer: permissions.has(PermissionAction.ContainersDeleteHierarchy),
        downloadFiles: permissions.has(PermissionAction.FilesDownload),
        modifyFileMetadata: permissions.has(PermissionAction.FilesModifyMetadata),
        moveFiles: permissions.has(PermissionAction.FilesMove),
        renameFiles: permissions.has(PermissionAction.FilesMove),
        tasksModify: permissions.has(PermissionAction.TasksModify),
        uploadFiles: permissions.has(PermissionAction.FilesCreateUpload) || permissions.has(PermissionAction.FilesCreateSingle),
        viewMetadata: permissions.has(PermissionAction.ContainersViewMetadata),
        viewNotes: permissions.has(PermissionAction.NotesView),
      };
    });
    this.subscriptions.messages = this.translate.getBundle('acquisitionFiles').subscribe(messages => {
      this.messages = messages;
    });

    this.subscriptions.fileTypes = this.configService.getFileTypes().subscribe(fileTypes => {
      this.fileTypes = sortBy(Object.keys(fileTypes), fileType => fileType.toLowerCase());
    });
    this.subscriptions.renames = this.state.fileRename$.subscribe(fileId => {
      const file = (this.acquisition?.files ?? []).find(f => f.file_id === fileId);
      if (file) {
        this.startFileRename(file);
      }
    });
    this.fileViewParameters$ = this.acquisitionFiles$.pipe(
      startWith([] as File[]),
      map(files => ({
        fileIds: files.map(file => file._id),
      })),
    );
  }

  ngAfterViewInit(): void {
    setTimeout(() => { this.shouldShowHoverIcons() }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.acquisition) {
      if (this.acquisition?._id && (changes.acquisition.firstChange || changes.acquisition.currentValue?._id !== changes.acquisition.previousValue?._id)) {
        const projectId = getContainerProjectId(this.acquisition);
        this.subscriptions.associatedApps = this.viewerService.getAssociatedApps({ container: this.acquisition, projectId }).pipe(first()).subscribe(apps => {
          this.viewerApps = apps;
        });
      }
      if (changes.acquisition.firstChange || changes.acquisition.currentValue?.label !== changes.acquisition.previousValue?.label) {
        this.stagingForm.get('label').reset(this.acquisition.label);
      }
      if (!this.files && (changes.acquisition.firstChange || changes.acquisition.currentValue?.files !== changes.acquisition.previousValue?.files)) {
        this.acquisitionFiles$.next(this.acquisition?.files ?? []);
      }
    }
    if (changes.files) {
      this.acquisitionFiles$.next(this.files ?? []);
    }
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  get isStagedAcquisition(): boolean {
    return !this.acquisition._id;
  }

  get hasPendingRenames(): boolean {
    return Object.keys(this.fileRenames).length > 0;
  }

  get timestamp(): string {
    const timestamp = this.acquisition?.timestamp;
    if (isEmpty(timestamp)) {
      return '';
    }
    const timezone = this.acquisition.timezone || this.session?.timezone;
    const fullFormatted = this.sessionService.formattedTimestamp({ timestamp, timezone });
    if (this.sessionService.showTimezone$.value) {
      return fullFormatted;
    }
    let components = fullFormatted.split(' ');
    if (this.session?.timezone === timezone && components.length === 3) {
      // timezone is already omitted if showTimezone$ is false, but just in case
      components = components.slice(0, -1); // omit timezone
    }
    const sessionDate = (this.session?.timestamp ?? '').split('T')[0];
    const acquisitionDate = timestamp.split('T')[0];
    if (sessionDate === acquisitionDate) {
      components = components.slice(1); // omit date
    }
    return components.join(' ');
  }

  getFileClassification(file: File): string {
    return this.sessionService.getFileClassification(file);
  }

  getFileOrigin(file: File): OriginInfo {
    return this.sessionService.getFileOrigin(file, this.acquisition['join-origin']);
  }

  isQuarantined(file: File): boolean {
    return file.virus_scan?.state === 'quarantined';
  }

  async selectFile(event: MouseEvent, file: File): Promise<void> {
    const selectedFileIds = await firstValueFrom(this.state.fileSelection$);
    if (event.shiftKey) {
      if (selectedFileIds.has(file.file_id)) {
        selectedFileIds.delete(file.file_id);
      } else {
        selectedFileIds.add(file.file_id);
      }
    } else {
      selectedFileIds.clear();
    }
    this.state.fileSelection$.next(selectedFileIds);
  }

  information(): void {
    this.dialog
      .open(AcquisitionInfoDialog, {
        data: {
          acquisition: this.acquisition,
        },
      })
      .afterClosed()
      .pipe(filter((result: AcquisitionInfoDialogResult) => Boolean(result?.dirty)))
      .subscribe(() => {
        this.sessionService.acquisitionChange$.next(null);
      });
  }

  downloadFiles(): void {
    const nodes = [{ level: 'acquisition', _id: this.acquisition._id }];
    this.dialog.open(FileDownloadDialog, {
      data: { requestBody: { nodes } },
    });
  }

  delete(): void {
    this.dialog.open(DeleteDialogComponent, {
      data: { content: `Are you sure you want to delete the acquisition "${this.acquisition.label || this.acquisition._id}"?` },
    }).afterClosed().pipe(
      filter((result: DeleteDialogResult) => Boolean(result.delete)),
      switchMap(result => this.coreApi.containers.delete<Acquisition>(this.acquisition._id, result.deleteReason ? { delete_reason: result.deleteReason } : {})),
    ).subscribe({
      next: () => {
        this.notifications.success('acquisition.delete.success');
        this.sessionService.acquisitionChange$.next(null);
      },
      error: (error: HttpErrorResponse) => {
        const messages = {
          'error': 'acquisition.delete.error', // generic error
          'analysis_conflict': 'acquisition.delete.analysisConflict',
          'original_data_present': 'acquisition.delete.originalData',
        };
        const reason = get(error, 'error.reason');
        if (reason in messages) {
          this.notifications.error(messages[reason]);
        } else {
          this.notifications.error(messages['error']);
        }
      },
    });
  }

  createFile(): void {
    this.containerAttachments.createFile(this.acquisition).then(() => {
      this.sessionService.acquisitionChange$.next(null);
    });
  }

  uploadFiles(): void {
    this.dialog.open(AcquisitionUploadDialog, {
      data: {
        session: this.session,
        acquisition: this.acquisition,
      },
    }).afterClosed()
      .pipe(filter(proceed => Boolean(proceed)))
      .subscribe(() => {
        this.sessionService.acquisitionChange$.next(null);
      });
  }

  notes(): void {
    this.dialog.open(AcquisitionNotesDialog, {
      data: {
        acquisition: this.acquisition,
      },
    });
  }

  protected createReaderTask(): void {
    this.fwDialog.openDialog(CreateReaderTaskDialogComponent, {
      closeOnDestroy: true,
      disableClose: true,
      data: {
        projectId: getContainerProjectId(this.acquisition),
        containerType: ReaderTaskContainerType.Acquisition,
        containerId: this.acquisition._id,
      },
    }).closed.pipe(take(1)).subscribe();
  }

  viewAcquisition(app: ViewerApp): void {
    this.viewerService.view({
      app: app,
      container: this.acquisition,
    }).subscribe(result => {
      if (result?.dirty) {
        this.sessionService.acquisitionChange$.next(null);
      }
    });
  }

  startFileRename(file: File): void {
    if (!this.permissions.renameFiles) {
      return;
    }
    this.fileRenames = {
      ...this.fileRenames,
      [file.file_id]: new FormControl(file.name, [
        Validators.required,
        validFileName,
        prohibitValues([...this.acquisition.files.map(file => file.name), file.name]),
      ]),
    };
  }

  cancelFileRename(file: File): void {
    this.fileRenames = omit(this.fileRenames, file.file_id);
    if (file.parents.acquisition !== this.acquisition._id) {
      this.state.fileMove$.next([{ file, acquisitionId: file.parents.acquisition }]);
    }
  }

  saveFileRename(file: File): void {
    const control = this.fileRenames[file.file_id];
    if (control.invalid) {
      return;
    }
    if (this.isStagedAcquisition) {
      this.fileRenames = omit(this.fileRenames, file.file_id);
      file.name = control.value;
      return;
    }
    const renamePayload: FileMoveBody = {
      name: control.value,
      run_gear_rules: this.state.runGearRulesOnMove$.value ?? false,
    };
    if (this.acquisition._id !== file.parents.acquisition) {
      renamePayload.container_reference = { type: ContainerType.Acquisition, id: this.acquisition._id };
    }
    this.coreApi.files.move(file.file_id, renamePayload).subscribe({
      next: () => {
        this.fileRenames = omit(this.fileRenames, file.file_id);
        file.name = control.value;
        file.parents.acquisition = this.acquisition._id;
      },
      error: error => {
        this.logger.error(error);
        this.notifications.error('files.rename.error');
      },
    });
  }

  toggleTypeSelect(file: File): void {
    if (!this.permissions.modifyFileMetadata) {
      return;
    }
    if (file.file_id in this.typeChanges) {
      this.typeChanges = omit(this.typeChanges, file.file_id);
    } else {
      this.typeChanges = { ...this.typeChanges, [file.file_id]: new FormControl(file.type) };
    }
  }

  saveFileTypeSelection(file: File): void {
    this.coreApi.files.put(file.file_id, { type: this.typeChanges[file.file_id].value }).subscribe({
      next: () => {
        file.type = this.typeChanges[file.file_id].value;
        this.toggleTypeSelect(file);
      },
      error: (error) => {
        this.notifications.error('files.changeType.error');
        this.logger.error(error);
      },
    });
  }

  getErrorMessage(fileId: string): string {
    const control = this.fileRenames[fileId];
    if (control.hasError('required')) {
      return this.messages.get('filenameRequired');
    }
    if (control.hasError('pattern')) {
      return this.messages.get('filenamePattern');
    }
    if (control.hasError('prohibitValues')) {
      return this.messages.get('filenameConflict');
    }
    return '';
  }

  async saveStagedAcquisition(): Promise<void> {
    if (!this.stagingForm.valid || this.hasPendingRenames) {
      return;
    }

    let acquisitionId: string;
    try {
      const acquisition = await firstValueFrom(this.coreApi.acquisitions.post({
        label: this.stagingForm.get('label').value,
        session: this.session._id,
      }));
      acquisitionId = acquisition._id;
    } catch (error) {
      if (error.status === 409) {
        this.notifications.error('acquisition.create.duplicate');
      } else {
        this.logger.error(error);
        this.notifications.error('acquisition.create.error');
      }
      return;
    }

    const moveOutcomes = await firstValueFrom(
      tryOperations(this.acquisition.files, file => this.coreApi.files.move(file.file_id, {
        container_reference: { type: ContainerType.Acquisition, id: acquisitionId },
        name: file.name,
        run_gear_rules: this.state.runGearRulesOnMove$.value ?? false,
      }))
    );
    const movedFiles = moveOutcomes.filter(outcome => outcome.result).map(outcome => outcome.source);
    for (const file of movedFiles) {
      file.parents.acquisition = acquisitionId;
    }
    const failedFiles = moveOutcomes.filter(outcome => outcome.error).map(outcome => outcome.source);
    if (failedFiles.length > 0) {
      this.logger.error(moveOutcomes.find(outcome => outcome.error).error);
      this.notifications.error(movedFiles.length > 0 ? 'files.move.partialError' : 'files.move.error');
    } else {
      this.notifications.success('files.move.newAcquisition');
    }
    this.stagedChanged.emit(acquisitionId);
  }

  cancelStagedAcquisition(): void {
    this.stagedChanged.emit(null);
  }

  showCvatIcon(file: File): boolean {
    return this.features.cvat && Boolean(file.cvat);
  }

  getCvatTooltip(file: File): string {
    return file.cvat.error_summary || this.CVATSyncStateTooltipMap[file.cvat.sync_state];
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.showHoverIcons = false;
    setTimeout(() => { this.shouldShowHoverIcons() }, 0);
  }

  shouldShowHoverIcons(): void {
    const fileTypeContainerLocationArray = this.fileTypeContainer?.toArray()
    const fileActionsLocationsArray = this.fileActionsElement.toArray()

    let show = true

    for (let i = 0; i < fileTypeContainerLocationArray.length; i++) {
      const fileTypeContainerLocation = fileTypeContainerLocationArray[i]?.nativeElement.getBoundingClientRect();
      const fileActionsLocations = fileActionsLocationsArray[i]?.nativeElement.getBoundingClientRect();
      const horizontalDistance = Math.abs(fileActionsLocations.left - fileTypeContainerLocation.right);

      if (horizontalDistance < 110) {
        show = false;
        break;
      }
    }
    this.showHoverIcons = show
  }

}
